/**
 * wireUp.ts — glue between the ChatSidebar component and the agent-bridge.
 * Exposes a single `wireChatSidebar(root, opts)` function so the openDAW
 * studio entry stays a one-liner mount call.
 *
 * Reactivity model: openDAW's JSX runtime is non-reactive (it returns raw
 * HTMLElements). We keep local mutable state and re-render the subtree by
 * rebuilding the element on every change. The sidebar is small (≤ a few
 * dozen DOM nodes), so this is fine for now.
 */

import { ChatSidebar, type ChatMessage, type ThinkingStep, type RecipeStep } from "./ChatSidebar.js";
import { bootstrap as bootstrapBridge, type AgentBridge } from "../agent-bridge/index.js";
import { serializeSession } from "../agent-bridge/serializer.js";
import type { StudioContextLike } from "../agent-bridge/handlers/store.js";
import { tryCatch, isDefined, isAbsent } from "@opendaw/lib-std";
import "./styles.css";

export interface WireOptions {
    /** Agent service WebSocket URL, e.g. `ws://localhost:8000/v1/session/default`. */
    wssUrl: string;
    /** Returns the current JWT on demand. Called fresh on every WebSocket (re)connect. */
    getJwt?: () => string | Promise<string>;
    studioContext?: StudioContextLike;
    /** Returns the current project ID for per-project chat persistence, or null. */
    getProjectId?: () => string | null;
}

export interface WireHandle {
    close: () => void;
    bridge: AgentBridge;
}

export function wireChatSidebar(root: HTMLElement, opts: WireOptions): WireHandle {
    const getJwt = opts.getJwt ?? (() => localStorage.getItem('sonn_jwt') ?? '');
    let currentProjectId: string | null = opts.getProjectId?.() ?? null;

    const state: {
        messages: ChatMessage[];
        isThinking: boolean;
        currentAssistant: string;
        currentThinking: ThinkingStep[];
        pendingRecipe: RecipeStep[] | null;
        streamBuffer: string;
        streamIntervalId: ReturnType<typeof setInterval> | null;
    } = {
        messages: currentProjectId !== null ? loadMessages(currentProjectId) : [],
        isThinking: false,
        currentAssistant: "",
        currentThinking: [],
        pendingRecipe: null,
        streamBuffer: "",
        streamIntervalId: null,
    };

    let bridge: AgentBridge;

    const save = (): void => {
        if (currentProjectId !== null) saveMessages(currentProjectId, state.messages);
    };

    const syncProjectId = (): void => {
        const newId = opts.getProjectId?.() ?? null;
        if (newId === currentProjectId) return;
        if (currentProjectId !== null) saveMessages(currentProjectId, state.messages);
        currentProjectId = newId;
        state.messages = newId !== null ? loadMessages(newId) : [];
        state.currentAssistant = "";
        state.isThinking = false;
        state.currentThinking = [];
        state.pendingRecipe = null;
        stopStream();
    };

    const getProjectSnapshot = (): string | undefined => {
        if (isAbsent(opts.studioContext)) return undefined;
        const p = serializeSession(opts.studioContext.getProject());
        return JSON.stringify({
            bpm: p.transport.tempo_bpm,
            time_signature: p.transport.time_signature,
            key: p.transport.key,
            length_seconds: Math.round(p.transport.length_seconds),
            tracks: p.tracks.map(track => ({
                id: track.id,
                name: track.name,
                type: track.type,
                volume_db: Math.round(track.volume_db * 10) / 10,
                pan: Math.round(track.pan * 100) / 100,
                mute: track.mute,
                solo: track.solo,
                insert_count: track.inserts.length,
                insert_plugins: track.inserts.map(ins => ins.plugin_id),
            })),
        });
    };

    const buildHistoryHint = (): ReadonlyArray<{ role: string; content: string }> =>
        state.messages
            .slice(0, -1)
            .filter(msg => msg.role === "user" || msg.role === "assistant")
            .slice(-40)
            .map(msg => ({ role: msg.role, content: msg.text }));

    const findThinkingStep = (name: string): ThinkingStep | undefined =>
        [...state.currentThinking].reverse().find(step => step.name === name && step.status === "pending");

    const formatResult = (result: unknown): string => {
        if (typeof result === 'string') return result.slice(0, 120)
        if (result !== null && typeof result === 'object') {
            const r = result as Record<string, unknown>
            const msg = r['message']
            if (typeof msg === 'string') return msg.slice(0, 120)
            const summary = r['summary']
            if (typeof summary === 'string') return summary.slice(0, 120)
        }
        return ''
    };

    const stopStream = (): void => {
        if (state.streamIntervalId !== null) {
            clearInterval(state.streamIntervalId);
            state.streamIntervalId = null;
        }
        if (state.streamBuffer.length > 0) {
            state.currentAssistant += state.streamBuffer;
            state.streamBuffer = "";
        }
    };

    const startStreamDrip = (): void => {
        if (state.streamIntervalId !== null) return;
        const CHARS_PER_TICK = Math.ceil(40 * 0.025); // ~40 chars/sec at 25ms interval
        state.streamIntervalId = setInterval(() => {
            if (state.streamBuffer.length === 0) return;
            const chunk = state.streamBuffer.slice(0, CHARS_PER_TICK);
            state.streamBuffer = state.streamBuffer.slice(CHARS_PER_TICK);
            state.currentAssistant += chunk;
            rerender();
        }, 25);
    };

    const send = (text: string): void => {
        syncProjectId();
        state.messages.push({ id: makeId(), role: "user", text, ts: Date.now() });
        save();
        state.isThinking = true;
        state.currentThinking = [];
        state.pendingRecipe = null;
        stopStream();
        rerender();
        const historyHint = buildHistoryHint();
        const projectSnapshot = getProjectSnapshot();
        void bridge.chatSend(text, historyHint, projectSnapshot).catch((err: unknown) => {
            state.isThinking = false;
            state.currentThinking = [];
            state.messages.push({
                id: makeId(),
                role: "system",
                text: `Send failed: ${err instanceof Error ? err.message : String(err)}`,
                ts: Date.now(),
            });
            save();
            rerender();
        });
    };

    const rerender = (): void => {
        syncProjectId();
        const streamingText = state.currentAssistant;
        const visibleMessages: ReadonlyArray<ChatMessage> =
            streamingText.length > 0
                ? [
                      ...state.messages,
                      {
                          id: "__streaming__",
                          role: "assistant" as const,
                          text: streamingText,
                          ts: Date.now(),
                          recipe: state.pendingRecipe ?? undefined,
                          isStreaming: state.streamBuffer.length > 0 || state.streamIntervalId !== null,
                      },
                  ]
                : state.messages;

        while (root.firstChild) {
            root.removeChild(root.firstChild);
        }
        root.appendChild(
            ChatSidebar({
                messages: visibleMessages,
                isThinking: state.isThinking && streamingText.length === 0,
                liveThinking: state.currentThinking,
                sessionId: currentProjectId ?? undefined,
                hasProject: currentProjectId !== null,
                onSend: send,
            }),
        );
        const feed = root.querySelector(".sonn-chat__feed");
        if (feed) feed.scrollTop = feed.scrollHeight;
    };

    bridge = bootstrapBridge({
        wssUrl: opts.wssUrl,
        getJwt,
        ...(isDefined(opts.studioContext) ? { studioContext: opts.studioContext } : {}),
    });

    bridge.onAgentEvent((event) => {
        if (event.type === "token") {
            state.isThinking = false;
            state.streamBuffer += event.text;
            startStreamDrip();
        } else if (event.type === "tool_call_started") {
            state.currentThinking.push({ name: event.name, status: "pending", args: event.arguments, reasoning: event.reasoning });
            rerender();
        } else if (event.type === "tool_call_succeeded") {
            const step = findThinkingStep(event.name);
            if (isDefined(step)) { step.status = "done"; step.result = formatResult(event.result); }
            rerender();
        } else if (event.type === "tool_call_failed") {
            const step = findThinkingStep(event.name);
            if (step) { step.status = "failed"; step.error = event.error; }
            rerender();
        } else if (event.type === "final_text") {
            stopStream();
            const finalText = event.text.length > 0 ? event.text : state.currentAssistant;
            state.messages.push({
                id: makeId(),
                role: "assistant",
                text: finalText,
                thinking: state.currentThinking.length > 0 ? [...state.currentThinking] : undefined,
                recipe: state.pendingRecipe ?? undefined,
                ts: Date.now(),
            });
            save();
            state.currentAssistant = "";
            state.isThinking = false;
            state.currentThinking = [];
            state.pendingRecipe = null;
            rerender();
        }
    });

    const projectPoll = setInterval(() => {
        const newId = opts.getProjectId?.() ?? null;
        if (newId !== currentProjectId) {
            syncProjectId();
            rerender();
        }
    }, 500);

    rerender();

    return {
        bridge,
        close: () => {
            clearInterval(projectPoll);
            stopStream();
            bridge.close();
        },
    };
}

function makeId(): string {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
        return crypto.randomUUID();
    }
    return Math.random().toString(36).slice(2);
}

function storageKey(projectId: string): string {
    return `ai-chat:${projectId}`;
}

function saveMessages(projectId: string, msgs: ChatMessage[]): void {
    tryCatch(() => localStorage.setItem(storageKey(projectId), JSON.stringify(msgs)));
}

function loadMessages(projectId: string): ChatMessage[] {
    const {status, value} = tryCatch(() => {
        const raw = localStorage.getItem(storageKey(projectId));
        return raw !== null ? (JSON.parse(raw) as ChatMessage[]) : [];
    });
    return status === "success" ? value : [];
}
